/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Bond-graph enumeration helpers for crystal MD. Given a flat
 * `RadialBond[]` list (built from a distance cutoff over the expanded
 * supercell), produce the implied:
 *
 *   - `AngularBond[]`: every 3-atom chain A-B-C where (A,B) and (B,C)
 *     are radial bonds. Constrains the molecule's bond angles so the
 *     valence geometry survives thermal motion (without this, an
 *     A-B-C molecule under MD would fold up because nothing resists
 *     the angle change once the radial bonds are at equilibrium).
 *
 *   - `TorsionalBond[]`: every 4-atom chain A-B-C-D. Models the
 *     hindrance of rotation around the central B-C bond — required
 *     for molecules like ethane, benzene, peptide chains where the
 *     dihedral has a preferred value.
 *
 * Equilibrium angle / dihedral default to the current geometry, so the
 * supercell starts at zero bonded force and thermal motion fluctuates
 * around the as-built configuration. This is the same convention the
 * molecule MD uses for radial bonds.
 *
 * Performance note: angular enumeration is O(Σ deg_i² / 2) and
 * torsional is O(Σ_{(b,c) in bonds} deg_b × deg_c). For supercells up
 * to ~500 atoms with typical valences (≤4 for covalent, ≤6 for
 * molecular packing) this is < 10k bonded terms — well within the MD
 * loop's per-step budget. No cap is applied; if a pathological
 * distance cutoff returns over-connected pairs the user can scale
 * down the supercell or fix the cutoff first.
 */
import { Atom } from '../Atom.ts';
import { AngularBond } from '../AngularBond.ts';
import { RadialBond } from '../RadialBond.ts';
import { TorsionalBond } from '../TorsionalBond.ts';
import { applyTorsionParameters } from '../torsionParamLookup.ts';

/**
 * Result of bond-graph enumeration. Caller hands these straight into
 * `new CrystalDynamics(atoms, container, radial, angular, torsional)`.
 */
export interface BondGraph {
  angular: AngularBond[];
  torsional: TorsionalBond[];
}

/**
 * Build an undirected adjacency list keyed by atom index. The atoms
 * array must be the same order CrystalViewer uses (and the radial
 * bonds must reference those same Atom instances); we look up an
 * atom's index by `indexOf`, which is O(n) but called only during
 * adjacency build — fine for the supercell sizes we target.
 */
const buildAdjacency = (atoms: Atom[], radialBonds: RadialBond[]): number[][] => {
  // Pre-index atoms by identity for O(1) lookup. Map<Atom, number>
  // because the Atom class doesn't store its own gallery index in a
  // stable way during supercell expansion.
  const idx = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) idx.set(atoms[i], i);
  const adj: number[][] = atoms.map(() => []);
  for (const rb of radialBonds) {
    const i = idx.get(rb.atom1);
    const j = idx.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    adj[i].push(j);
    adj[j].push(i);
  }
  return adj;
};

/**
 * Enumerate angular + torsional bonds implied by the radial bond
 * graph. De-duplicated by canonicalizing each tuple (A-B-C uses the
 * smaller of A's / C's index as the leading atom; A-B-C-D uses the
 * lexicographically smaller of (A,B,C,D) vs (D,C,B,A) since the two
 * are physically the same dihedral).
 */
export const enumerateBonds = (atoms: Atom[], radialBonds: RadialBond[]): BondGraph => {
  const angular: AngularBond[] = [];
  const torsional: TorsionalBond[] = [];
  if (radialBonds.length === 0) return { angular, torsional };

  const adj = buildAdjacency(atoms, radialBonds);

  // ---- Angular: every B with degree ≥ 2 contributes C(deg, 2) angles.
  // We dedupe by emitting only when neighbor[0] < neighbor[1] — the
  // tuple is undirected, so (A,B,C) and (C,B,A) describe the same angle.
  for (let b = 0; b < adj.length; b++) {
    const neigh = adj[b];
    if (neigh.length < 2) continue;
    for (let p = 0; p < neigh.length; p++) {
      const a = neigh[p];
      for (let q = p + 1; q < neigh.length; q++) {
        const c = neigh[q];
        angular.push(new AngularBond(atoms[a], atoms[b], atoms[c]));
      }
    }
  }

  // Helper: is the angle at vertex p2 (between p1 and p3) within
  // COLLINEAR_TOL of 0° or 180°? Such configurations make the dihedral
  // around the central bond physically undefined — the cross product
  // n = b₁ × b₂ vanishes when b₁ ∥ b₂, so atan2(...) returns
  // noise-driven values and the torsion potential injects spurious
  // energy on every step. sp1 atoms (e.g. nitrile C, alkyne C, sp1 N)
  // intrinsically sit at 180°, so torsions through them MUST be
  // excluded. Threshold |cos θ| > 0.985 ⇒ θ < 10° or θ > 170°, which
  // catches sp1 (180°) cleanly while leaving sp2 (120°) and sp3
  // (109.5°) far inside the safe band.
  const COLLINEAR_COS = 0.985;
  const isNearCollinear = (a: number, b: number, c: number): boolean => {
    const p1 = atoms[a].position;
    const p2 = atoms[b].position;
    const p3 = atoms[c].position;
    const ux = p1.x - p2.x;
    const uy = p1.y - p2.y;
    const uz = p1.z - p2.z;
    const vx = p3.x - p2.x;
    const vy = p3.y - p2.y;
    const vz = p3.z - p2.z;
    const ul = Math.hypot(ux, uy, uz);
    const vl = Math.hypot(vx, vy, vz);
    if (ul === 0 || vl === 0) return true;
    const cosTheta = (ux * vx + uy * vy + uz * vz) / (ul * vl);
    return Math.abs(cosTheta) > COLLINEAR_COS;
  };

  // ---- Torsional: for each radial bond (b,c), walk neighbors of b
  // (excluding c) and neighbors of c (excluding b) to form A-B-C-D.
  // Dedupe by requiring B < C in the tuple order (the bond list is
  // undirected so each pair would otherwise be visited twice).
  for (const rb of radialBonds) {
    const b = atoms.indexOf(rb.atom1);
    const c = atoms.indexOf(rb.atom2);
    if (b < 0 || c < 0) continue;
    // Canonicalize: only emit dihedrals where the central bond's first
    // atom has the lower index. (B, C) and (C, B) describe the same
    // four-atom chain after a left-right flip — skipping one of them
    // halves the work and matches the molecule MD's convention.
    if (b >= c) continue;
    const neighB = adj[b];
    const neighC = adj[c];
    for (const a of neighB) {
      if (a === c) continue;
      // Skip if A-B-C is near-collinear (B is sp1-like). Dihedral around
      // (B,C) bond is undefined when b₁ = A-B and b₂ = B-C are parallel.
      if (isNearCollinear(a, b, c)) continue;
      for (const d of neighC) {
        if (d === b || d === a) continue;
        // Same check for the other end: B-C-D near-collinear ⇒ C sp1-like.
        if (isNearCollinear(b, c, d)) continue;
        torsional.push(new TorsionalBond(atoms[a], atoms[b], atoms[c], atoms[d]));
      }
    }
  }

  // Overlay polymer-relevant OPLS-style FF pack on enumerated torsions.
  // Quadruples not in TORSION_PARAMETERS keep the engine defaults
  // (strength = 1, periodicity = 1) and the as-built equilibrium dihedral.
  applyTorsionParameters(torsional);

  return { angular, torsional };
};

/**
 * Connected-components labeling over the radial bond graph. Returns
 * `componentOfAtom[i]` = cluster ID for atom `i`. Atoms with no bonds
 * get their own one-atom cluster.
 *
 * Used by CrystalViewer to map a click on any atom to "select the
 * whole molecule" — for an ice supercell each H2O is a cluster, so
 * clicking either H or the O selects all three atoms.
 */
export const labelMolecules = (atoms: Atom[], radialBonds: RadialBond[]): number[] => {
  const idx = new Map<Atom, number>();
  for (let i = 0; i < atoms.length; i++) idx.set(atoms[i], i);
  const adj: number[][] = atoms.map(() => []);
  for (const rb of radialBonds) {
    const i = idx.get(rb.atom1);
    const j = idx.get(rb.atom2);
    if (i === undefined || j === undefined) continue;
    adj[i].push(j);
    adj[j].push(i);
  }
  const label: number[] = new Array(atoms.length).fill(-1);
  let next = 0;
  // Iterative BFS — recursive DFS would blow the stack on large
  // supercells with long covalent chains.
  for (let start = 0; start < atoms.length; start++) {
    if (label[start] !== -1) continue;
    const id = next++;
    const queue: number[] = [start];
    label[start] = id;
    while (queue.length > 0) {
      const v = queue.shift()!;
      for (const w of adj[v]) {
        if (label[w] === -1) {
          label[w] = id;
          queue.push(w);
        }
      }
    }
  }
  return label;
};
